import React, { useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import { Calendar, Clock } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";
import { DASHBOARD_FIELD, DASHBOARD_THEME } from "./dashboardTheme";
import {
  formatCivilDateTime,
  parseCivilDateTime,
  withLockedAttendanceDate,
} from "../../utils/dateUtils";

/**
 * Shared datetime picker — Patient indigo field chrome (Dashboard / Payroll).
 *
 * Values are civil datetime-local strings (YYYY-MM-DDTHH:mm) in attendance
 * timezone wall-clock (America/Los_Angeles). They are not converted to the
 * browser timezone.
 *
 * @param {string} [lockDate] YYYY-MM-DD — when set, calendar day cannot change.
 */
const DateTimePicker = ({
  value,
  onChange,
  placeholder = "Select date and time",
  dateFormat = "MM-dd-yyyy, hh:mm aa",
  lockDate,
  disabled = false,
}) => {
  useEffect(() => {
    if (!document.getElementById("datepicker-portal")) {
      const portal = document.createElement("div");
      portal.id = "datepicker-portal";
      document.body.appendChild(portal);
    }
    return () => {
      const portal = document.getElementById("datepicker-portal");
      if (portal && portal.children.length === 0) {
        portal.remove();
      }
    };
  }, []);

  const lockedDay = useMemo(() => {
    if (!lockDate || !/^\d{4}-\d{2}-\d{2}$/.test(lockDate)) return null;
    const [y, m, d] = lockDate.split("-").map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  }, [lockDate]);

  const selectedDate = parseCivilDateTime(
    lockDate ? withLockedAttendanceDate(value, lockDate) : value,
  );

  const handleDateChange = (date) => {
    if (!onChange) return;
    if (!date) {
      onChange("");
      return;
    }
    let next = formatCivilDateTime(date);
    if (lockDate) {
      next = withLockedAttendanceDate(next, lockDate);
    }
    onChange(next);
  };

  return (
    <div className="relative">
      <DatePicker
        selected={selectedDate}
        onChange={handleDateChange}
        showTimeSelect
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        scrollableYearDropdown
        yearDropdownItemNumber={100}
        timeIntervals={1}
        dateFormat={dateFormat}
        placeholderText={placeholder}
        className={DASHBOARD_FIELD.input}
        calendarClassName="!shadow-lg !border-indigo-200 !rounded-xl"
        popperClassName="!z-[100]"
        minDate={lockedDay || undefined}
        maxDate={lockedDay || undefined}
        disabled={disabled}
        popperModifiers={[
          {
            name: "preventOverflow",
            options: {
              rootBoundary: "viewport",
              tether: false,
              altAxis: true,
            },
          },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["bottom", "top", "right", "left"],
            },
          },
        ]}
        portalId="datepicker-portal"
        withPortal
        timeCaption="Time"
        timeFormat="hh:mm aa"
        wrapperClassName="w-full"
        popperPlacement="bottom-start"
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-1.5">
        <Calendar className={`w-3.5 h-3.5 ${DASHBOARD_THEME.section.icon}`} />
        <Clock className={`w-3.5 h-3.5 ${DASHBOARD_THEME.section.icon}`} />
      </div>
    </div>
  );
};

export default DateTimePicker;
